import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type PosRoom,
  type PosRoomNode,
  type PosRoomReviewItem,
  useCreatePosRoomMutation,
  useDismissPosRoomReviewMutation,
  useGetPosRoomNodesQuery,
  useGetPosRoomQuery,
  useGetPosRoomReviewsQuery,
  useGetPosRoomsQuery,
  useIssuePosRoomCommandMutation,
  useResolvePosRoomReviewMutation,
  useUpdatePosRoomMutation,
} from '@/store/api/posRoomsApi';

type Section = 'overview' | 'nodes' | 'commands' | 'logs' | 'reviews' | 'ranges';
type ReviewDecision = 'resolve' | 'dismiss';
type NodeCommand = 'disable' | 'enable';

interface ProvisionedRoom {
  name: string;
  hmacKey: string;
}

const sections: Array<{ id: Section; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'nodes', label: 'Nodes' },
  { id: 'commands', label: 'Commands' },
  { id: 'logs', label: 'Logs' },
  { id: 'reviews', label: 'Review queue' },
  { id: 'ranges', label: 'Receipt ranges' },
];

export function PosRoomsPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [section, setSection] = useState<Section>('overview');
  const [reviewStatus, setReviewStatus] = useState<'open' | 'resolved' | 'dismissed'>('open');
  const [pendingReview, setPendingReview] = useState<{ item: PosRoomReviewItem; decision: ReviewDecision } | null>(null);
  const [pendingNode, setPendingNode] = useState<{ node: PosRoomNode; command: NodeCommand } | null>(null);
  const [roomActionError, setRoomActionError] = useState<string | null>(null);
  const [roomActionMessage, setRoomActionMessage] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [decisionMessage, setDecisionMessage] = useState<string | null>(null);
  const [decisionIsError, setDecisionIsError] = useState(false);
  const [commandMessage, setCommandMessage] = useState<string | null>(null);
  const [updateRoom, updateState] = useUpdatePosRoomMutation();
  const [issueCommand, commandState] = useIssuePosRoomCommandMutation();
  const [resolveReview, resolveState] = useResolvePosRoomReviewMutation();
  const [dismissReview, dismissState] = useDismissPosRoomReviewMutation();

  const {
    data: rooms,
    isLoading: isLoadingRooms,
    isError: isRoomsError,
    refetch: refetchRooms,
  } = useGetPosRoomsQuery(undefined, { skip: Boolean(roomId) });
  const {
    data: room,
    isLoading: isLoadingRoom,
    isError: isRoomError,
    refetch: refetchRoom,
  } = useGetPosRoomQuery(roomId ?? '', { skip: !roomId });
  const {
    data: nodes,
    isLoading: isLoadingNodes,
    isError: isNodesError,
    refetch: refetchNodes,
  } = useGetPosRoomNodesQuery(roomId ?? '', { skip: !roomId });
  const {
    data: reviews,
    isLoading: isLoadingReviews,
    isError: isReviewsError,
    refetch: refetchReviews,
  } = useGetPosRoomReviewsQuery(
    { roomId: roomId ?? '', status: reviewStatus, limit: 100 },
    { skip: !roomId || section !== 'reviews' },
  );

  if (!roomId) {
    return (
      <PosRoomList
        rooms={rooms}
        isLoading={isLoadingRooms}
        isError={isRoomsError}
        onRetry={() => void refetchRooms()}
      />
    );
  }

  if (isLoadingRoom) return <LoadingRows label="Loading POS room" columns={4} />;
  if (isRoomError || !room) {
    return <LoadError label="POS room" onRetry={() => void refetchRoom()} />;
  }

  const currentRoom = updateState.data?.id === room.id ? { ...room, ...updateState.data } : room;

  const changeRoomStatus = async () => {
    const nextStatus = currentRoom.status === 'active' ? 'disabled' : 'active';
    setRoomActionError(null);
    setRoomActionMessage(null);
    try {
      await updateRoom({ roomId, body: { status: nextStatus } }).unwrap();
      setRoomActionMessage(`${currentRoom.name} is now ${nextStatus}.`);
    } catch {
      setRoomActionError(`Could not confirm the ${nextStatus === 'disabled' ? 'disable' : 'enable'} update for ${currentRoom.name}. Check the room status before retrying.`);
    }
  };

  const saveRoomSettings = async (body: Pick<PosRoom, 'name' | 'settings'>) => {
    setSettingsError(null);
    setSettingsMessage(null);
    try {
      await updateRoom({ roomId, body }).unwrap();
      setSettingsMessage('Room settings saved.');
      return true;
    } catch {
      setSettingsError('Could not save room settings. Your inputs are retained; check them and retry.');
      return false;
    }
  };

  const submitReviewDecision = async () => {
    if (!pendingReview) return;
    const { item, decision } = pendingReview;
    setDecisionMessage(null);
    setDecisionIsError(false);
    try {
      if (decision === 'resolve') await resolveReview({ roomId, itemId: item.id }).unwrap();
      else await dismissReview({ roomId, itemId: item.id }).unwrap();
      setDecisionMessage(`Review ${item.id} marked ${decision === 'resolve' ? 'resolved' : 'dismissed'}.`);
      setPendingReview(null);
    } catch {
      setDecisionIsError(true);
      setDecisionMessage(`Could not ${decision} review ${item.id}. It may have changed; refresh the queue before another decision.`);
      setPendingReview(null);
    }
  };

  const refreshReviewQueue = async () => {
    const result = await refetchReviews();
    if (!result.error) {
      setDecisionIsError(false);
      setDecisionMessage(null);
    }
  };

  const submitNodeCommand = async () => {
    if (!pendingNode) return;
    const { node, command } = pendingNode;
    setCommandMessage(null);
    try {
      const result = await issueCommand({
        roomId,
        body: { type: command, targetNodeId: node.nodeId, expiresInSeconds: 900 },
      }).unwrap();
      setCommandMessage(`Command ${result.id} recorded as ${result.status}. Admin command completion is not exposed.`);
      setPendingNode(null);
      void refetchNodes();
    } catch (error) {
      const code = commandErrorCode(error);
      if (code === 'target_required') {
        setCommandMessage('Command rejected: target_required. Refresh the node list and select a registered node.');
      } else if (code === 'node_stale') {
        setCommandMessage('Command rejected: node_stale. Refresh node state before enabling this node.');
      } else {
        setCommandMessage(`Could not confirm whether the ${command} command for node ${node.nodeId} was recorded. Refresh node state before retrying; this endpoint has no idempotency key.`);
      }
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild><Link to="/pos-rooms">← POS rooms</Link></Button>
      <header className="space-y-1">
        <h1 className="text-2xl font-bold">{currentRoom.name}</h1>
        <p className="text-sm text-muted-foreground">Room {currentRoom.id} · Store {currentRoom.storeId}</p>
      </header>
      <nav aria-label="POS room sections" className="flex flex-wrap gap-2 border-b pb-3">
        {sections.map(({ id, label }) => (
          <Button key={id} type="button" variant={section === id ? 'secondary' : 'ghost'} aria-current={section === id ? 'page' : undefined} onClick={() => setSection(id)}>
            {label}
          </Button>
        ))}
      </nav>
      {section === 'overview' && <RoomOverview
        room={currentRoom}
        nodeCount={nodes?.length}
        nodesLoading={isLoadingNodes}
        nodesError={isNodesError}
        onRetryNodes={() => void refetchNodes()}
        onChangeStatus={() => void changeRoomStatus()}
        isUpdating={updateState.isLoading}
        actionError={roomActionError}
        actionMessage={roomActionMessage}
        settingsError={settingsError}
        settingsMessage={settingsMessage}
        onSaveSettings={saveRoomSettings}
      />}
      {section === 'nodes' && <NodeList
        nodes={nodes}
        roomStatus={currentRoom.status}
        isLoading={isLoadingNodes}
        isError={isNodesError}
        onRetry={() => void refetchNodes()}
        onCommand={(node, command) => setPendingNode({ node, command })}
        pendingNode={pendingNode}
        onCancelCommand={() => setPendingNode(null)}
        onConfirmCommand={() => void submitNodeCommand()}
        commandPending={commandState.isLoading}
        commandMessage={commandMessage}
        commandIsError={Boolean(commandMessage?.startsWith('Could not') || commandMessage?.startsWith('Command rejected:'))}
      />}
      {section === 'commands' && <UnavailableSurface title="Commands" message="Command history is not exposed in the admin API." detail="The service can issue supported node controls, but only edge nodes can poll and acknowledge commands." />}
      {section === 'logs' && <UnavailableSurface title="Logs" message="Log listing and export are not exposed in the admin API." />}
      {section === 'ranges' && <UnavailableSurface title="Receipt ranges" message="Receipt range lease inspection is not exposed in the admin API." detail="The lease endpoint requires an edge room key and is not an admin read endpoint." />}
      {section === 'reviews' && <ReviewQueue
        reviews={reviews}
        status={reviewStatus}
        onStatusChange={setReviewStatus}
        isLoading={isLoadingReviews}
        isError={isReviewsError}
        onRetry={() => void refreshReviewQueue()}
        pendingReview={pendingReview}
        onDecision={(item, decision) => {
          setDecisionMessage(null);
          setDecisionIsError(false);
          setPendingReview({ item, decision });
        }}
        onCancelDecision={() => setPendingReview(null)}
        onConfirmDecision={() => void submitReviewDecision()}
        isSubmitting={resolveState.isLoading || dismissState.isLoading}
        message={decisionMessage}
        messageIsError={decisionIsError}
        actionBlocked={decisionIsError}
      />}
    </div>
  );
}

function PosRoomList({
  rooms,
  isLoading,
  isError,
  onRetry,
}: {
  rooms?: PosRoom[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [storeId, setStoreId] = useState('');
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ProvisionedRoom | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [createRoom, createState] = useCreatePosRoomMutation();

  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    try {
      const result = await createRoom({ storeId: storeId.trim(), name: name.trim() }).unwrap();
      setCreated({ name: result.name, hmacKey: result.hmacKey });
      createState.reset();
      setStoreId('');
      setName('');
    } catch {
      setFormError('Could not create this room. Check the store ID and your access, then retry.');
    }
  };

  return (
    <div className="space-y-6">
      <header><h1 className="text-2xl font-bold">POS rooms</h1><p className="text-sm text-muted-foreground">Operate provisioned rooms and inspect their reported edge nodes.</p></header>
      {created && <div role="status" aria-live="polite" className="space-y-2 rounded-md border border-warning/50 p-4">
        <p className="font-medium">Room {created.name} created. Save this room key now; it is shown only once.</p>
        <code className="block break-all rounded bg-muted p-3">{created.hmacKey}</code>
        <Button type="button" variant="outline" onClick={() => setCreated(null)}>I have saved the key</Button>
      </div>}
      <section className="space-y-3 border-y py-4" aria-labelledby="create-pos-room-heading">
        <h2 id="create-pos-room-heading" className="text-lg font-semibold">Create POS room</h2>
        <form className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={(event) => void create(event)}>
          <Field label="Room name" id="pos-room-name"><Input id="pos-room-name" value={name} onChange={(event) => setName(event.target.value)} required maxLength={200} /></Field>
          <Field label="Store ID" id="pos-room-store"><Input id="pos-room-store" value={storeId} onChange={(event) => setStoreId(event.target.value)} required maxLength={200} /></Field>
          <Button type="submit" disabled={!name.trim() || !storeId.trim() || createState.isLoading}>{createState.isLoading ? 'Creating…' : 'Create room'}</Button>
        </form>
      {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
      </section>
      <p className="text-sm text-muted-foreground">The room API returns an unpaginated list; results are organization-scoped by the service.</p>
      <section aria-busy={isLoading}>
        {isLoading ? <LoadingRows label="Loading POS rooms" columns={4} />
          : isError ? <LoadError label="POS rooms" onRetry={onRetry} />
            : !rooms?.length ? <EmptyState title="No POS rooms yet." description="Create a room and assign it to a store to provision POS edge infrastructure." />
              : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="POS rooms list">
                <table className="w-full min-w-[38rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Room</th><th scope="col" className="px-4 py-3">Store ID</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3">Room ID</th></tr></thead>
                  <tbody>{rooms.map((room) => <tr key={room.id} className="border-t"><td className="px-4 py-3"><Link className="font-medium text-primary underline-offset-4 hover:underline" to={`/pos-rooms/${encodeURIComponent(room.id)}`}>{room.name}</Link></td><td className="px-4 py-3">{room.storeId}</td><td className="px-4 py-3">{safeStatus(room.status)}</td><td className="px-4 py-3">{room.id}</td></tr>)}</tbody>
                </table>
              </div>}
      </section>
    </div>
  );
}

function RoomOverview({
  room,
  nodeCount,
  nodesLoading,
  nodesError,
  onRetryNodes,
  onChangeStatus,
  isUpdating,
  actionError,
  actionMessage,
  settingsError,
  settingsMessage,
  onSaveSettings,
}: {
  room: PosRoom;
  nodeCount?: number;
  nodesLoading: boolean;
  nodesError: boolean;
  onRetryNodes: () => void;
  onChangeStatus: () => void;
  isUpdating: boolean;
  actionError: string | null;
  actionMessage: string | null;
  settingsError: string | null;
  settingsMessage: string | null;
  onSaveSettings: (body: Pick<PosRoom, 'name' | 'settings'>) => Promise<boolean>;
}) {
  const [confirmStatus, setConfirmStatus] = useState<'active' | 'disabled' | null>(null);
  const nextStatus = room.status === 'active' ? 'disabled' : 'active';
  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-lg font-semibold">Overview</h2><p className="text-sm">Reported room status: <strong>{safeStatus(room.status)}</strong></p></div>
        {room.status === 'active' || room.status === 'disabled'
          ? <Button type="button" variant={nextStatus === 'disabled' ? 'destructive' : 'outline'} disabled={isUpdating} onClick={() => setConfirmStatus(nextStatus)}>
              {nextStatus === 'disabled' ? 'Disable room' : 'Enable room'}
            </Button>
          : <p className="text-sm text-muted-foreground">Room controls unavailable for an unrecognized status.</p>}
      </div>
      {confirmStatus && <div role="group" aria-label="Confirm room change" className="space-y-3 rounded-md border border-destructive/40 p-4">
        <p>Confirm {confirmStatus === 'disabled' ? 'disabling' : 'enabling'} room <strong>{room.name}</strong> ({room.id}). This changes the room status used by POS edge APIs.</p>
        <div className="flex gap-2"><Button type="button" disabled={isUpdating} onClick={() => { onChangeStatus(); setConfirmStatus(null); }}>Confirm {confirmStatus} room</Button><Button type="button" variant="outline" onClick={() => setConfirmStatus(null)}>Cancel</Button></div>
      </div>}
      {actionError && <p role="alert" className="text-sm text-destructive">{actionError}</p>}
      {actionMessage && <p role="status" aria-live="polite" className="text-sm">{actionMessage}</p>}
      <dl className="grid gap-4 border-y py-4 sm:grid-cols-2 lg:grid-cols-4">
        <Detail label="Store ID" value={room.storeId} /><Detail label="Room ID" value={room.id} />
        <Detail label="Order retention" value={room.settings?.orderRetentionDays === undefined ? 'Unknown' : `${room.settings.orderRetentionDays} days`} />
        <Detail label="Edge nodes" value={nodesLoading ? 'Loading…' : nodesError ? 'Unavailable' : nodeCount === undefined ? 'Unknown' : String(nodeCount)} />
      </dl>
      {nodesError && <p role="alert" className="text-sm text-destructive">Could not load the room node count. <Button variant="outline" onClick={onRetryNodes}>Try again</Button></p>}
      {room.status !== 'active' && <p className="text-sm text-muted-foreground">{room.status === 'disabled' ? 'Room is disabled' : 'Room status is unknown'}; node control commands are unavailable.</p>}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">Offline card caps</h3>
        <p className="text-sm text-muted-foreground">The room API returns minor-unit amounts but no currency. Values are shown exactly as returned; currency is not inferred.</p>
        <dl className="grid gap-4 sm:grid-cols-3">
          <Detail label="Per transaction" value={minorUnits(room.settings?.cardCaps?.perTxnMinor)} />
          <Detail label="Daily" value={minorUnits(room.settings?.cardCaps?.dailyMinor)} />
          <Detail label="Manager approval above" value={minorUnits(room.settings?.cardCaps?.managerApprovalAboveMinor)} />
        </dl>
        <Detail label="Review tolerance" value={room.settings?.reviewTolerance === undefined ? 'Unknown' : String(room.settings.reviewTolerance)} />
      </section>
      <RoomSettingsForm room={room} isSaving={isUpdating} error={settingsError} message={settingsMessage} onSave={onSaveSettings} />
    </section>
  );
}

function RoomSettingsForm({
  room,
  isSaving,
  error,
  message,
  onSave,
}: {
  room: PosRoom;
  isSaving: boolean;
  error: string | null;
  message: string | null;
  onSave: (body: Pick<PosRoom, 'name' | 'settings'>) => Promise<boolean>;
}) {
  const [validationError, setValidationError] = useState<string | null>(null);

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    const form = new FormData(event.currentTarget);
    const name = String(form.get('name') ?? '').trim();
    const retention = Number(form.get('orderRetentionDays'));
    const perTxnMinor = parseMinorUnitInput(String(form.get('perTxnMinor') ?? ''));
    const dailyMinor = parseMinorUnitInput(String(form.get('dailyMinor') ?? ''));
    const managerApprovalAboveMinor = parseMinorUnitInput(String(form.get('managerApprovalAboveMinor') ?? ''));
    const reviewTolerance = Number(form.get('reviewTolerance'));
    if (!name) return setValidationError('Enter a room name.');
    if (!Number.isInteger(retention) || retention < 1 || retention > 365) return setValidationError('Order retention must be between 1 and 365 days.');
    if (perTxnMinor === null || dailyMinor === null || managerApprovalAboveMinor === null) {
      return setValidationError('Card caps must be whole, non-negative minor-unit integers within the API safe-number range.');
    }
    if (!Number.isFinite(reviewTolerance) || reviewTolerance < 0) return setValidationError('Review tolerance must be a non-negative number.');
    await onSave({
      name,
      settings: {
        orderRetentionDays: retention,
        cardCaps: { perTxnMinor, dailyMinor, managerApprovalAboveMinor },
        reviewTolerance,
      },
    });
  };

  return (
    <section className="space-y-3 border-t pt-4" aria-labelledby="room-settings-heading">
      <h3 id="room-settings-heading" className="text-base font-semibold">Edit room settings</h3>
      <p className="text-sm text-muted-foreground">Card caps are submitted as integer minor units because that is the room API contract. The API does not return a currency.</p>
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void save(event)}>
        <Field label="Room name" id="pos-room-edit-name"><Input id="pos-room-edit-name" name="name" defaultValue={room.name} required maxLength={200} /></Field>
        <Field label="Order retention days" id="pos-room-edit-retention"><Input id="pos-room-edit-retention" name="orderRetentionDays" type="number" min="1" max="365" step="1" defaultValue={room.settings?.orderRetentionDays ?? ''} required /></Field>
        <Field label="Per-transaction card cap (minor units)" id="pos-room-edit-per-txn"><Input id="pos-room-edit-per-txn" name="perTxnMinor" inputMode="numeric" defaultValue={minorInputValue(room.settings?.cardCaps?.perTxnMinor)} required /></Field>
        <Field label="Daily card cap (minor units)" id="pos-room-edit-daily"><Input id="pos-room-edit-daily" name="dailyMinor" inputMode="numeric" defaultValue={minorInputValue(room.settings?.cardCaps?.dailyMinor)} required /></Field>
        <Field label="Manager approval threshold (minor units)" id="pos-room-edit-approval"><Input id="pos-room-edit-approval" name="managerApprovalAboveMinor" inputMode="numeric" defaultValue={minorInputValue(room.settings?.cardCaps?.managerApprovalAboveMinor)} required /></Field>
        <Field label="Review tolerance" id="pos-room-edit-tolerance"><Input id="pos-room-edit-tolerance" name="reviewTolerance" type="number" min="0" step="any" defaultValue={room.settings?.reviewTolerance ?? ''} required /></Field>
        <div className="space-y-2 sm:col-span-2">
          <Button type="submit" disabled={isSaving}>{isSaving ? 'Saving…' : 'Save room settings'}</Button>
          {validationError && <p role="alert" className="text-sm text-destructive">{validationError}</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {message && <p role="status" aria-live="polite" className="text-sm">{message}</p>}
        </div>
      </form>
    </section>
  );
}

function NodeList({
  nodes,
  roomStatus,
  isLoading,
  isError,
  onRetry,
  onCommand,
  pendingNode,
  onCancelCommand,
  onConfirmCommand,
  commandPending,
  commandMessage,
  commandIsError,
}: {
  nodes?: PosRoomNode[];
  roomStatus: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onCommand: (node: PosRoomNode, command: NodeCommand) => void;
  pendingNode: { node: PosRoomNode; command: NodeCommand } | null;
  onCancelCommand: () => void;
  onConfirmCommand: () => void;
  commandPending: boolean;
  commandMessage: string | null;
  commandIsError: boolean;
}) {
  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-lg font-semibold">Nodes</h2><p className="text-sm text-muted-foreground">Node state and freshness come from the service heartbeat registry.</p></div><Button type="button" variant="outline" disabled={isLoading} onClick={onRetry}>{isLoading ? 'Refreshing…' : 'Refresh nodes'}</Button></header>
      {roomStatus !== 'active' && <p className="text-sm text-muted-foreground">{roomStatus === 'disabled' ? 'Room is disabled' : 'Room status is unknown'}; node control commands are unavailable.</p>}
      {commandMessage && <p role={commandIsError ? 'alert' : 'status'} aria-live="polite" className={commandIsError ? 'text-sm text-destructive' : 'text-sm'}>{commandMessage}</p>}
      {pendingNode && <div role="group" aria-label="Confirm node command" className="space-y-3 rounded-md border border-destructive/40 p-4">
        <p>Confirm {pendingNode.command} for node <strong>{pendingNode.node.nodeId}</strong> in this room. The edge node may be isolated or re-enabled.{pendingNode.node.isStale === true && pendingNode.command === 'disable' ? ' Heartbeat is stale; disable can still be queued.' : ''}</p>
        <div className="flex gap-2"><Button type="button" disabled={commandPending} onClick={onConfirmCommand}>{commandPending ? 'Sending…' : `Send ${pendingNode.command} command`}</Button><Button type="button" variant="outline" disabled={commandPending} onClick={onCancelCommand}>Cancel</Button></div>
      </div>}
      {isLoading ? <LoadingRows label="Loading edge nodes" columns={6} />
        : isError ? <LoadError label="edge nodes" onRetry={onRetry} />
          : !nodes?.length ? <EmptyState title="No edge nodes reported." description="Nodes appear after an edge node sends its first heartbeat." />
            : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="POS edge nodes">
              <table className="w-full min-w-[58rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Node</th><th scope="col" className="px-4 py-3">Role</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3">Epoch</th><th scope="col" className="px-4 py-3">Last sequence</th><th scope="col" className="px-4 py-3">Heartbeat ({localTimeZone()})</th><th scope="col" className="px-4 py-3">Control</th></tr></thead>
                   <tbody>{nodes.map((node) => <tr key={node.id} className="border-t"><td className="px-4 py-3">{node.label || node.nodeId}<span className="block text-xs text-muted-foreground">{node.nodeId}</span></td><td className="px-4 py-3">{safeStatus(node.role)}</td><td className="px-4 py-3">{safeStatus(node.status)}</td><td className="px-4 py-3">{node.currentEpoch ?? 'Unknown'}</td><td className="px-4 py-3 tabular-nums">{node.lastSeq ?? 'Unknown'}</td><td className="px-4 py-3">{heartbeatStatus(node)}</td><td className="px-4 py-3"><Button type="button" size="sm" variant="outline" disabled={roomStatus !== 'active' || (node.status !== 'running' && node.status !== 'disabled') || (node.status === 'disabled' && node.isStale === true)} onClick={() => onCommand(node, node.status === 'disabled' ? 'enable' : 'disable')}>{node.status === 'disabled' ? `Enable node ${node.nodeId}` : `Disable node ${node.nodeId}`}</Button>{node.isStale === true && <p className="mt-1 text-xs text-muted-foreground">{node.status === 'disabled' ? 'Enable is unavailable while this node\'s heartbeat is stale.' : 'Disable remains available for stale nodes and may be queued.'}</p>}</td></tr>)}</tbody>
              </table>
            </div>}
      <p className="text-sm text-muted-foreground">The node API returns an unpaginated list. Unreported fields remain Unknown.</p>
    </section>
  );
}

function ReviewQueue({
  reviews,
  status,
  onStatusChange,
  isLoading,
  isError,
  onRetry,
  pendingReview,
  onDecision,
  onCancelDecision,
  onConfirmDecision,
  isSubmitting,
  message,
  messageIsError,
  actionBlocked,
}: {
  reviews?: PosRoomReviewItem[];
  status: 'open' | 'resolved' | 'dismissed';
  onStatusChange: (status: 'open' | 'resolved' | 'dismissed') => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  pendingReview: { item: PosRoomReviewItem; decision: ReviewDecision } | null;
  onDecision: (item: PosRoomReviewItem, decision: ReviewDecision) => void;
  onCancelDecision: () => void;
  onConfirmDecision: () => void;
  isSubmitting: boolean;
  message: string | null;
  messageIsError: boolean;
  actionBlocked: boolean;
}) {
  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-lg font-semibold">Review queue</h2><p className="text-sm text-muted-foreground">Only service-supported resolve and dismiss decisions are available.</p></div><Field label="Review status" id="review-status"><select id="review-status" className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(event) => onStatusChange(event.target.value as typeof status)}><option value="open">Open</option><option value="resolved">Resolved</option><option value="dismissed">Dismissed</option></select></Field></header>
      {message && <div role={messageIsError ? 'alert' : 'status'} aria-live={messageIsError ? 'assertive' : 'polite'} className={messageIsError ? 'text-sm text-destructive' : 'text-sm'}>{message}{messageIsError && <Button type="button" variant="outline" onClick={onRetry}>Refresh review queue</Button>}</div>}
      {pendingReview && <div role="group" aria-label="Confirm review decision" className="space-y-3 rounded-md border p-4"><p>Confirm {pendingReview.decision} for review <strong>{pendingReview.item.id}</strong> ({reviewKindLabel(pendingReview.item)})?</p><div className="flex gap-2"><Button type="button" disabled={isSubmitting} onClick={onConfirmDecision}>{isSubmitting ? 'Saving…' : `Confirm ${pendingReview.decision} review`}</Button><Button type="button" variant="outline" disabled={isSubmitting} onClick={onCancelDecision}>Cancel</Button></div></div>}
      {isLoading ? <LoadingRows label="Loading review queue" columns={5} />
        : isError ? <LoadError label="review queue" onRetry={onRetry} />
          : !reviews?.length ? <EmptyState title={status === 'open' ? 'No open review items.' : `No ${status} review items.`} description="Only review items returned by this room's review API are shown." />
            : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="POS room review items">
              <table className="w-full min-w-[44rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Type</th><th scope="col" className="px-4 py-3">Severity</th><th scope="col" className="px-4 py-3">Context</th><th scope="col" className="px-4 py-3">Created</th><th scope="col" className="px-4 py-3">Decision</th></tr></thead>
                <tbody>{reviews.map((item) => <tr key={item.id} className="border-t"><td className="px-4 py-3"><span className="font-medium">{reviewKindLabel(item)}</span><span className="block text-xs text-muted-foreground">{item.id}</span></td><td className="px-4 py-3">{safeStatus(item.severity)}</td><td className="px-4 py-3">{reviewContext(item.payload)}</td><td className="px-4 py-3">{formatDate(item.createdAt)}</td><td className="px-4 py-3">{item.status === 'open' ? <div className="flex flex-wrap gap-2"><Button type="button" size="sm" variant="outline" disabled={actionBlocked} onClick={() => onDecision(item, 'resolve')}>Resolve review {item.id}</Button><Button type="button" size="sm" variant="ghost" disabled={actionBlocked} onClick={() => onDecision(item, 'dismiss')}>Dismiss review {item.id}</Button></div> : safeStatus(item.status)}</td></tr>)}</tbody>
              </table>
            </div>}
      <p className="text-sm text-muted-foreground">Showing up to 100 review items; this API has no cursor or offset pagination.</p>
    </section>
  );
}

function UnavailableSurface({ title, message, detail }: { title: string; message: string; detail?: string }) {
  return <section className="space-y-2"><h2 className="text-lg font-semibold">{title}</h2><EmptyState title="Not exposed in admin" description={message} />{detail && <p className="text-sm text-muted-foreground">{detail}</p>}</section>;
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 break-words text-sm">{value}</dd></div>;
}

function LoadError({ label, onRetry }: { label: string; onRetry: () => void }) {
  return <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load {label}. <Button variant="outline" onClick={onRetry}>Try again</Button></div>;
}

function minorUnits(value?: number | string): string {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? `${String(value)} minor units` : 'Unavailable (unsafe numeric amount)';
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) return `${BigInt(value).toString()} minor units`;
  return 'Unknown';
}

function minorInputValue(value?: number | string): string {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value >= 0 ? String(value) : '';
  return typeof value === 'string' && /^\d+$/.test(value) ? value : '';
}

function parseMinorUnitInput(value: string): number | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const integer = BigInt(value);
  if (integer > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(integer);
}

function safeStatus(value?: string | null): string {
  if (!value) return 'Unknown';
  return humanize(value);
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ');
}

function heartbeatStatus(node: PosRoomNode): string {
  if (node.isStale === true) return `Stale · ${formatDate(node.lastHeartbeatAt)}`;
  if (node.isStale === false && node.lastHeartbeatAt) return `Reported · ${formatDate(node.lastHeartbeatAt)}`;
  return 'Heartbeat unavailable';
}

function commandErrorCode(error: unknown): 'target_required' | 'node_stale' | null {
  if (!error || typeof error !== 'object' || !('data' in error)) return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const code = 'code' in data ? data.code : 'message' in data ? data.message : undefined;
  return code === 'target_required' || code === 'node_stale' ? code : null;
}

function formatDate(value?: string | null): string {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function localTimeZone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
}

function reviewKindLabel(item: PosRoomReviewItem): string {
  return item.kind === 'conflict_unresolved' && item.payload?.kind === 'poison_command'
    ? 'Poison command (conflict unresolved)'
    : humanize(item.kind);
}

function reviewContext(payload?: Record<string, unknown>): string {
  if (!payload) return 'No safe context fields returned';
  const fields = ['nodeId', 'seq', 'epoch'] as const;
  const context = fields.flatMap((field) => {
    const value = payload[field];
    return typeof value === 'string' || typeof value === 'number' ? [`${field}: ${String(value)}`] : [];
  });
  return context.length ? context.join(' · ') : 'No safe context fields returned';
}
